import { t } from "@travily/i18n";
import { accountSettingsPath, creatorListingsPath, favoritesPath, messagesPath } from "@travily/routes";
import { Icon, type IconName } from "@ui/components/Icon";
import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import { tv } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";

const menuView = tv({
  slots: {
    section: "border-b border-neutral-100 last:border-b-0",
    sectionInner: "flex flex-col py-2",
    row: [
      "flex w-full items-center gap-3 px-5 py-3 text-left text-base text-neutral-900 outline-none",
      "hover:bg-neutral-50 focus-visible:bg-neutral-50 active:bg-neutral-100",
    ],
    rowIcon: "flex size-6 items-center justify-center text-neutral-500",
    rowLabel: "flex-1",
    rowChevron: "size-5 text-neutral-400",
  },
});

const styles = menuView();

// Mobile full-page menu: prefetch row targets on mount (no hover on touch)
// so the next page is warm by the time the user taps.

interface MenuRowProps {
  icon: IconName;
  label: string;
  href?: string;
  onPress?: () => void;
  chevron?: boolean;
}

function MenuRow({ icon, label, href, onPress, chevron = true }: MenuRowProps) {
  const content = (
    <>
      <span aria-hidden="true" className={styles.rowIcon()}>
        <Icon name={icon} size="24" />
      </span>
      <span className={styles.rowLabel()}>{label}</span>
      {chevron ? (
        <span aria-hidden="true" className={styles.rowChevron()}>
          <Icon name="right" size="20" />
        </span>
      ) : null}
    </>
  );

  if (href) {
    return (
      <Link cacheFor={NAV_PREFETCH_CACHE_FOR} className={styles.row()} href={href} prefetch="mount">
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onPress} className={styles.row()}>
      {content}
    </button>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <div className={styles.section()}>
      <div className={styles.sectionInner()}>{children}</div>
    </div>
  );
}

export interface MenuViewProps {
  /** Current account mode — controls whether the mode toggle row reads "Switch to explorer" or "Switch to creator". */
  mode: "explorer" | "creator";
  /** True when the current account has at least one listing — drives the explorer-mode CTA. */
  hasListings: boolean;
  /** Called when the "Languages & currency" row is pressed. */
  onLanguagesAndCurrency: () => void;
  /** Called when the "Get Help" row is pressed (opens the support messenger). */
  onGetHelp: () => void;
  /** Called when "Log out" is pressed. */
  onLogout: () => void;
}

export function MenuView({ mode, hasListings, onLanguagesAndCurrency, onGetHelp, onLogout }: MenuViewProps) {
  return (
    <div className="flex flex-col">
      <Section>
        {mode === "creator" ? (
          <MenuRow icon="swap" label={t("components.frames.app_layout.switch_to_explorer")} href="/" />
        ) : hasListings ? (
          <MenuRow
            icon="swap"
            label={t("components.frames.app_layout.switch_to_creator")}
            href={creatorListingsPath()}
          />
        ) : null}
      </Section>

      <Section>
        <MenuRow icon="heart1" label={t("components.frames.app_layout.menu_items.favorites")} href={favoritesPath()} />
        <MenuRow icon="message" label={t("components.frames.app_layout.menu_items.messages")} href={messagesPath()} />
      </Section>

      <Section>
        <MenuRow
          icon="globe"
          label={t("components.frames.app_layout.menu_items.languages_and_currency")}
          onPress={onLanguagesAndCurrency}
          chevron={false}
        />
        <MenuRow
          icon="question"
          label={t("components.frames.app_layout.menu_items.get_help")}
          onPress={onGetHelp}
          chevron={false}
        />
        <MenuRow
          icon="settings"
          label={t("components.frames.app_layout.menu_items.settings")}
          href={accountSettingsPath()}
        />
      </Section>

      <Section>
        <MenuRow
          icon="exit"
          label={t("components.frames.app_layout.menu_items.log_out")}
          onPress={onLogout}
          chevron={false}
        />
      </Section>
    </div>
  );
}
